import { defineStore } from "pinia";
import { computed, ref } from "vue";
import {
  getProfile,
  type LoginChallenge,
  login as loginApi,
  loginWithDingTalkCode,
  logout as logoutApi,
  type AuthProfile,
} from "@/api/auth";
import { encryptLoginPayload } from "@/utils/login-crypto";

window.localStorage.removeItem("pms_access_token");
window.localStorage.removeItem("pms_refresh_token");

export const useAuthStore = defineStore("auth", () => {
  const profile = ref<AuthProfile | null>(null);
  const initialized = ref(false);
  const authenticated = computed(() => Boolean(profile.value));
  const isSystemAdmin = computed(
    () => profile.value?.roles.includes("system_admin") ?? false,
  );
  const can = (permission: string) =>
    isSystemAdmin.value ||
    Boolean(profile.value?.permissions.includes(permission));
  async function signIn(
    challenge: LoginChallenge,
    username: string,
    password: string,
    captcha: string,
  ) {
    const envelope = await encryptLoginPayload(challenge, {
      username,
      password,
      captcha,
    });
    const response = await loginApi(envelope);
    profile.value = response.data.profile;
    initialized.value = true;
    return profile.value;
  }
  async function load() {
    if (profile.value) return profile.value;
    try {
      const response = await getProfile();
      profile.value = response.data;
      return profile.value;
    } finally {
      initialized.value = true;
    }
  }
  async function signInWithDingTalk(authCode: string) {
    const response = await loginWithDingTalkCode(authCode);
    profile.value = response.data.profile;
    initialized.value = true;
    return profile.value;
  }
  async function signOut() {
    await logoutApi();
    profile.value = null;
    initialized.value = true;
  }
  return {
    profile,
    initialized,
    authenticated,
    isSystemAdmin,
    can,
    signIn,
    signInWithDingTalk,
    load,
    signOut,
  };
});
